/* eslint-disable no-param-reassign */
import { validatePhoneNumber } from "common/utils/validators/phone";
import { getValidatableFields } from "./address-fields";

/**
 * AddressFieldValidator
 *
 * Validates address form fields for the local delivery address editor.
 * Differs from Vue implementation: fullName and phoneNumber are OPTIONAL.
 *
 * @example
 * const isValid = AddressFieldValidator.validateForm(formData, errors);
 * const zipError = AddressFieldValidator.validateZipcode("90210");
 */
class AddressFieldValidator {
  /**
   * Error messages for validation failures
   * @type {Object.<string, string>}
   */
  static VALIDATION_MESSAGES = {
    FULL_NAME_INVALID: "Please enter at least two words",
    ADDRESS1_REQUIRED: "Address line 1 is required",
    ZIPCODE_REQUIRED: "ZIP code is required",
    ZIPCODE_INVALID: "Please enter a valid ZIP code",
    CITY_REQUIRED: "City is required",
    PHONE_NUMBER_INVALID: "Please enter a valid phone number"
  };

  /**
   * Regular expression for validating US/CA ZIP codes
   * Accepts 5-6 digits optionally followed by -XXXX
   * @type {RegExp}
   */
  static ZIPCODE_REGEX = /^\d{5,6}(-\d{4})?$/;

  /**
   * Validates the full name field
   * Note: Full name is OPTIONAL, but if provided, must have at least two words
   * @param {Object} formData - Form data object containing address fields
   * @param {string} formData.fullName - Full name to validate
   * @param {Object} errors - Error object to store validation errors
   * @returns {boolean} True if valid, false otherwise
   */
  static validateFullName(formData, errors) {
    const trimmedName = formData.fullName.trim();

    // Full name is optional, so empty is valid
    if (!trimmedName) {
      return true;
    }

    // If provided, must have at least two words
    const nameParts = trimmedName.split(/\s+/).filter(part => part.length > 0);
    if (nameParts.length < 2) {
      errors.fullName = AddressFieldValidator.VALIDATION_MESSAGES.FULL_NAME_INVALID;
      return false;
    }

    return true;
  }

  /**
   * Validates the address line 1 field
   * Ensures the field is not empty (REQUIRED)
   * @param {Object} formData - Form data object containing address fields
   * @param {string} formData.address1 - Address line 1 to validate
   * @param {Object} errors - Error object to store validation errors
   * @returns {boolean} True if valid, false otherwise
   */
  static validateAddress1(formData, errors) {
    if (!formData.address1.trim()) {
      errors.address1 = AddressFieldValidator.VALIDATION_MESSAGES.ADDRESS1_REQUIRED;
      return false;
    }

    return true;
  }

  /**
   * Validates the ZIP code field
   * Ensures the ZIP code matches US/CA format (5-6 digits with optional -XXXX)
   * @param {Object} formData - Form data object containing address fields
   * @param {string} formData.zipcode - ZIP code to validate
   * @param {Object} errors - Error object to store validation errors
   * @returns {boolean} True if valid, false otherwise
   */
  static validateZipcode(formData, errors) {
    const trimmedZipcode = formData.zipcode.trim();

    if (!trimmedZipcode) {
      errors.zipcode = AddressFieldValidator.VALIDATION_MESSAGES.ZIPCODE_REQUIRED;
      return false;
    }

    if (!AddressFieldValidator.ZIPCODE_REGEX.test(trimmedZipcode)) {
      errors.zipcode = AddressFieldValidator.VALIDATION_MESSAGES.ZIPCODE_INVALID;
      return false;
    }

    return true;
  }

  /**
   * Validates the city field
   * Ensures the field is not empty (REQUIRED)
   * @param {Object} formData - Form data object containing address fields
   * @param {string} formData.city - City to validate
   * @param {Object} errors - Error object to store validation errors
   * @returns {boolean} True if valid, false otherwise
   */
  static validateCity(formData, errors) {
    if (!formData.city.trim()) {
      errors.city = AddressFieldValidator.VALIDATION_MESSAGES.CITY_REQUIRED;
      return false;
    }

    return true;
  }

  /**
   * Validates the phone number field
   * Note: Phone number is OPTIONAL, but if provided, must match valid phone format
   * @param {Object} formData - Form data object containing address fields
   * @param {string} formData.phone - Phone number to validate
   * @param {Object} errors - Error object to store validation errors
   * @returns {boolean} True if valid, false otherwise
   */
  static validatePhoneNumber(formData, errors) {
    const trimmedPhone = formData.phone.trim();

    // Phone number is optional, so empty is valid
    if (!trimmedPhone) {
      return true;
    }

    // Use centralized phone validator
    if (!validatePhoneNumber(trimmedPhone)) {
      errors.phone = AddressFieldValidator.VALIDATION_MESSAGES.PHONE_NUMBER_INVALID;
      return false;
    }

    return true;
  }

  static #VALIDATORS = {
    fullName: AddressFieldValidator.validateFullName,
    address1: AddressFieldValidator.validateAddress1,
    zipcode: AddressFieldValidator.validateZipcode,
    city: AddressFieldValidator.validateCity,
    phone: AddressFieldValidator.validatePhoneNumber
  };

  /**
   * Validates a single field by field name
   * Clears existing error for the field before validating
   * @param {string} fieldName - Name of the field to validate (fullName, addressLine1, addressLine2, zipCode, city, phone)
   * @param {Object} formData - Form data object containing address fields
   * @param {Object} errors - Error object to store validation errors
   * @returns {boolean} True if valid, false otherwise
   */
  static validateField(fieldName, formData, errors) {
    errors[fieldName] = "";

    const validator = AddressFieldValidator.#VALIDATORS[fieldName];
    return validator ? validator(formData, errors) : true;
  }

  /**
   * Validates all required fields in the address form
   * Populates the errors object with validation messages for invalid fields
   * @param {Object} formData - Form data object containing address fields
   * @param {string} formData.fullName - Full name value (optional)
   * @param {string} formData.address1 - Address line 1 value (required)
   * @param {string} formData.address2 - Address line 2 value (optional)
   * @param {string} formData.zipcode - ZIP code value (required)
   * @param {string} formData.city - City value (required)
   * @param {string} formData.phone - Phone number value (optional)
   * @param {Object} errors - Error object to store validation errors
   * @returns {boolean} True if all fields are valid, false otherwise
   */
  static validateForm(formData, errors) {
    let allFieldsValid = true;
    const fieldsToValidate = getValidatableFields();

    for (const fieldName of fieldsToValidate) {
      if (!AddressFieldValidator.validateField(fieldName, formData, errors)) {
        allFieldsValid = false;
      }
    }

    return allFieldsValid;
  }
}

export default AddressFieldValidator;
